import { useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, UserPlus, Store } from "lucide-react";
import ContactsToolbar from "../../components/ContactsToolbar";
import EmptyState from "../../components/EmptyState";
import ReceivedInvitationCard from "../../components/ReceivedInvitationCard";
import SentInvitationCard from "../../components/SentInvitationCard";
import SuggestionCard from "../../components/SuggestionCard";
import useFriendRequests from "../../hooks/useFriendRequests";
import useFriendSuggestions from "../../hooks/useFriendSuggestions";
import { useContacts } from "../../contexts/ContactsContext";
import { useNotification } from "../../../../hooks/useNotification";
import shopApi from "../../../../api/shopApi";

const SectionHeader = ({ label, count, collapsed, onToggle }) => (
  <button
    onClick={onToggle}
    className="w-full flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold text-gray-700 hover:text-gray-900 transition-colors"
  >
    {collapsed ? (
      <ChevronRight size={14} className="text-gray-400" />
    ) : (
      <ChevronDown size={14} className="text-gray-400" />
    )}
    <span>{label}</span>
    {count != null && (
      <span className="text-gray-500 font-normal">({count})</span>
    )}
  </button>
);

const FriendRequestsTab = () => {
  const { selectedShopIds, shops, user } = useContacts();
  const { addNotification } = useNotification();
  const {
    items: received,
    loading: loadingReceived,
    reload: reloadReceived,
  } = useFriendRequests({ direction: "received" });
  const {
    items: sent,
    loading: loadingSent,
    reload: reloadSent,
  } = useFriendRequests({ direction: "sent" });
  const {
    items: suggestions,
    total: suggestionsTotal,
    loading: loadingSuggestions,
    hasMore: hasMoreSuggestions,
    showMore: showMoreSuggestions,
    reload: reloadSuggestions,
    removeById: removeSuggestionById,
  } = useFriendSuggestions({ pageSize: 20 });

  const [showReceived, setShowReceived] = useState(true);
  const [showSent, setShowSent] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  // Optimistic hide — request id đã action thành công, ẩn ngay không đợi reload
  const [hiddenIds, setHiddenIds] = useState(() => new Set());

  // Chỉ giữ status "đang chờ xử lý" cho 2 section này.
  const pendingReceived = useMemo(
    () =>
      received.filter(
        (r) => r.status === "received" && !hiddenIds.has(r.id),
      ),
    [received, hiddenIds],
  );
  const pendingSent = useMemo(
    () =>
      sent.filter((r) => r.status === "sent" && !hiddenIds.has(r.id)),
    [sent, hiddenIds],
  );

  const reloadAll = useCallback(() => {
    reloadReceived();
    reloadSent();
    reloadSuggestions();
  }, [reloadReceived, reloadSent, reloadSuggestions]);

  const buildShopParams = useCallback(
    (req) => {
      const shopId = req.shop?.id || req.shopId;
      const shop = shops.find((s) => s._id === shopId);
      if (!shop) return null;
      return {
        shopId: shop._id,
        zaloId: shop.zaloId,
        staffId: user?._id,
      };
    },
    [shops, user?._id],
  );

  const runAction = useCallback(
    async (req, apiCall, successMsg) => {
      const base = buildShopParams(req);
      if (!base) {
        addNotification(
          "Không tìm thấy thông tin tài khoản Zalo. Hãy đăng nhập lại shop.",
          -1,
        );
        return;
      }
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.add(req.id);
        return next;
      });
      try {
        const res = await apiCall({
          ...base,
          uid: req.partnerUid,
        });
        if (res?.status === 1) {
          setHiddenIds((prev) => {
            const next = new Set(prev);
            next.add(req.id);
            return next;
          });
          addNotification(successMsg, 1);
          // Reload nhẹ sau action để DB sync — không block UI
          setTimeout(reloadAll, 600);
        } else {
          addNotification(res?.message || "Thao tác thất bại", -1);
        }
      } catch (e) {
        addNotification(
          e?.response?.data?.message || e?.message || "Lỗi khi gọi API",
          -2,
        );
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(req.id);
          return next;
        });
      }
    },
    [buildShopParams, addNotification, reloadAll],
  );

  const handleAccept = (req) =>
    runAction(req, shopApi.acceptFriend, "Đã đồng ý lời mời kết bạn");
  const handleReject = (req) =>
    runAction(req, shopApi.rejectFriend, "Đã từ chối lời mời");
  const handleWithdraw = (req) =>
    runAction(req, shopApi.withdrawFriend, "Đã thu hồi lời mời");

  // Handlers cho suggestions section
  const handleSkipSuggestion = useCallback(
    (suggestion) => {
      removeSuggestionById(suggestion.userId, suggestion.shopId);
    },
    [removeSuggestionById],
  );

  const handleAddSuggestion = useCallback(
    async (suggestion) => {
      const shop = shops.find((s) => s._id === suggestion.shopId);
      if (!shop) {
        addNotification("Không tìm thấy tài khoản Zalo", -1);
        return;
      }
      const key = `${suggestion.shopId}:${suggestion.userId}`;
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      try {
        const res = await shopApi.addFriend({
          uid: suggestion.userId,
          contentAddFriend: "",
          shopId: shop._id,
          zaloId: shop.zaloId,
          staffId: user?._id,
        });
        if (res?.status === 1) {
          addNotification(
            res?.message === "Tự động kết bạn"
              ? "Đã kết bạn"
              : "Đã gửi lời mời kết bạn",
            1,
          );
          removeSuggestionById(suggestion.userId, suggestion.shopId);
        } else {
          addNotification(res?.message || "Kết bạn thất bại", -1);
        }
      } catch (e) {
        addNotification(
          e?.response?.data?.message || e?.message || "Lỗi khi gọi API",
          -2,
        );
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [shops, user?._id, addNotification, removeSuggestionById],
  );

  const loading = loadingReceived || loadingSent || loadingSuggestions;
  const isEmpty =
    !loading &&
    pendingReceived.length === 0 &&
    pendingSent.length === 0 &&
    suggestions.length === 0;

  return (
    <div className="flex flex-col h-full">
      <ContactsToolbar
        title="Lời mời kết bạn"
        subtitle={`${pendingReceived.length} đã nhận · ${pendingSent.length} đã gửi · ${suggestionsTotal} gợi ý`}
        onReload={reloadAll}
        loading={loading}
        showFilter={false}
      />

      <div className="flex-1 overflow-auto bg-gray-50">
        {selectedShopIds.length === 0 ? (
          <EmptyState
            icon={Store}
            title="Chưa chọn tài khoản Zalo"
            description="Chọn ít nhất 1 tài khoản từ dropdown phía trên."
          />
        ) : isEmpty ? (
          <EmptyState
            icon={UserPlus}
            title="Chưa có lời mời nào"
            description="Khi có người gửi lời mời kết bạn, hoặc bạn gửi đi, danh sách sẽ hiện ở đây."
          />
        ) : (
          <div className="pb-6">
            {/* Section: Lời mời đã nhận */}
            <SectionHeader
              label="Lời mời đã nhận"
              count={pendingReceived.length}
              collapsed={!showReceived}
              onToggle={() => setShowReceived((v) => !v)}
            />
            {showReceived && pendingReceived.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 px-4 pb-4">
                {pendingReceived.map((r) => {
                  const p = r.partner || {};
                  return (
                    <ReceivedInvitationCard
                      key={r.id}
                      avatar={p.avatar}
                      name={p.name || p.zaloName || r.partnerUid}
                      source="Từ số điện thoại"
                      createdAt={r.createdAt}
                      message={r.greetingMessage}
                      pending={pendingIds.has(r.id)}
                      onAccept={() => handleAccept(r)}
                      onReject={() => handleReject(r)}
                    />
                  );
                })}
              </div>
            )}
            {showReceived && pendingReceived.length === 0 && !loading && (
              <div className="px-4 pb-4 text-[12px] text-gray-400 italic">
                Chưa có lời mời nào.
              </div>
            )}

            {/* Section: Lời mời đã gửi */}
            <SectionHeader
              label="Lời mời đã gửi"
              count={pendingSent.length}
              collapsed={!showSent}
              onToggle={() => setShowSent((v) => !v)}
            />
            {showSent && pendingSent.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 px-4 pb-4">
                {pendingSent.map((r) => {
                  const p = r.partner || {};
                  return (
                    <SentInvitationCard
                      key={r.id}
                      avatar={p.avatar}
                      name={p.name || p.zaloName || r.partnerUid}
                      createdAt={r.createdAt}
                      pending={pendingIds.has(r.id)}
                      onWithdraw={() => handleWithdraw(r)}
                    />
                  );
                })}
              </div>
            )}
            {showSent && pendingSent.length === 0 && !loading && (
              <div className="px-4 pb-4 text-[12px] text-gray-400 italic">
                Chưa có lời mời nào.
              </div>
            )}

            {/* Section: Gợi ý kết bạn */}
            <SectionHeader
              label="Gợi ý kết bạn"
              count={suggestionsTotal}
              collapsed={!showSuggestions}
              onToggle={() => setShowSuggestions((v) => !v)}
            />
            {showSuggestions && suggestions.length > 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 px-4 pb-3">
                  {suggestions.map((s) => {
                    const key = `${s.shopId}:${s.userId}`;
                    return (
                      <SuggestionCard
                        key={key}
                        avatar={s.avatar}
                        name={s.displayName || s.zaloName || s.userId}
                        subtitle="Có thể bạn quen"
                        shopName={selectedShopIds.length > 1 ? s.shopName : null}
                        pending={pendingIds.has(key)}
                        onSkip={() => handleSkipSuggestion(s)}
                        onAddFriend={() => handleAddSuggestion(s)}
                      />
                    );
                  })}
                </div>
                {hasMoreSuggestions && (
                  <div className="flex justify-center pb-6">
                    <button
                      onClick={showMoreSuggestions}
                      className="px-4 h-9 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-[13px] text-gray-700"
                    >
                      Xem thêm
                    </button>
                  </div>
                )}
              </>
            )}
            {showSuggestions &&
              suggestions.length === 0 &&
              !loadingSuggestions && (
                <div className="px-4 pb-4 text-[12px] text-gray-400 italic">
                  Chưa có gợi ý nào.
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendRequestsTab;
